import { Attr, IndexedMesh, TypedArray } from "./indexed-mesh";
import { IndexedMeshWriter } from "./indexed-mesh-writer";
import { TWO_PI } from "../math/constants";


type DiscAttrs = {
    uv: Attr,
    normal: Attr,
}

export class Disc extends IndexedMeshWriter<DiscAttrs> {

    x: number;
    y: number;
    radius: number;

    n: number;

    /**
     * @param x - The X coordinate of the center of the disc
     * @param y - The Y coordinate of the center of the disc
     * @param radius
     * @param n - The count of vertices around the perimeter of the disc, which
     * happens to also be the number of facets.
     */
    constructor(x: number, y: number, radius: number, n: number) {
        super();
        this.x = x;
        this.y = y;
        this.radius = radius;
        this.n = n;
    }

    computeAttributeCounts(): IndexedMesh<DiscAttrs> {
        const mesh = new IndexedMesh({
            uv: new Attr(2),
            normal: new Attr(3),
        });

        const
            n = this.n,
            triangles = n,
            vertexes = n + 1;

        mesh.index.count = triangles * 3;
        mesh.position.count = vertexes;
        mesh.attrs.normal.count = vertexes;
        mesh.attrs.uv.count = vertexes;

        return mesh;
    }

    computeMeshData(mesh: IndexedMesh<DiscAttrs>) {
        disc(this.x, this.y, this.radius, this.n,
             mesh.position.data, 0,
             mesh.attrs.normal.data,
             mesh.attrs.uv.data, 0);
        discIdx(0, this.n, mesh.index.data, 0);
    }

}

/**
 * Write the vertecies of a disc to the given typed arrays. This always
 * advances `p` by (n+1)*3, and `u` by (n+1)*2.
 */
export const disc = (x: number, y: number, r: number, n: number,
                     pos: TypedArray, p: number,
                     norm: TypedArray, // norm starts at norm[p] too
                     uv: TypedArray, u: number) =>
{
    const
        inc = TWO_PI / n,
        vertexes = n + 1,
        nlimit = p + vertexes * 3;

    // First, do the normals, which are always (0, 0, 1)
    let ni = p; // ni means 'normal index'
    while(ni < nlimit) {
        norm[ni++] = 0; norm[ni++] = 0; norm[ni++] = 1;
    }

    pos[p++] = x;   pos[p++] = y; pos[p++] = 0;
    pos[p++] = x+r; pos[p++] = y; pos[p++] = 0;

    uv[u++] = x;   uv[u++] = y;
    uv[u++] = x+r; uv[u++] = y;

    // We've already written the center and the first vertex. We need to write
    // n-1 more vertices to have written all n perimeter vertices.
    for(let i = 1; i < n; i++) {
        const
            t = i*inc,
            vx = x + r*Math.cos(t),
            vy = y + r*Math.sin(t);

        pos[p++]  = vx; pos[p++]  = vy; pos[p++]  = 0;
         uv[u++]  = vx;  uv[u++]  = vy;
    }
}

/**
 * Computes the edges/triangles for a single disc. This is a purely topological
 * problem so none of the measurements are required, only the side/vertex count
 * of the disc (`n`).
 *
 * @param v0 - the element index of the first position, i.e. center, of the
 * disc. For a single disc, this will always be 0. For an array of discs, the
 * v0 advances by n+1 elements for each additional disc.
 * @param n - how many vertexes/sides around the perimeter of the disc.
 * @param idx - the mesh's index data
 * @param i - where to start adding triangles in the index array
 */
export const discIdx = (v0: number, n: number, idx: TypedArray, i: number) => {
    // We write n triangles. For v0 = 0, the pattern looks like [0, 1, 2],
    // [0, 2, 3], [0, 3, 4], etc...  This implies that there must be n+1
    // elements starting at v0
    for(let j = 1; j < n; j++) {
        idx[i++] = v0;
        idx[i++] = v0 + j;
        idx[i++] = v0 + j + 1;
    }
    // The last triangle uses the center, and the last and first vertices on
    // the perimeter.
    idx[i++] = v0;
    idx[i++] = v0 + n;
    idx[i++] = v0 + 1;
}
